import { useState, useEffect } from "react"
import useT from "utils/useT"
import Modal from "components/modal"
import { IoSparkles } from "react-icons/io5"
import useMediaQuery from "utils/useMediaQuery"
import { event } from "utils/gtag"
import useSnackbarStore from "stores/snackbar"

let hasShownInstallReadyToast = false

const attachWorkerStateListeners = (registration, onStateChange) => {
  if (!registration) {
    return
  }

  registration.installing?.addEventListener("statechange", () => {
    onStateChange?.({
      phase: registration.installing?.state,
      worker: "installing",
    })
  })

  registration.waiting?.addEventListener("statechange", () => {
    onStateChange?.({
      phase: registration.waiting?.state,
      worker: "waiting",
    })
  })

  registration.active?.addEventListener("statechange", () => {
    onStateChange?.({
      phase: registration.active?.state,
      worker: "active",
    })
  })
}

export default function WorkboxNotifications() {
  const t = useT()
  const isDesktop = useMediaQuery("(min-width: 1200px)")
  const [showModal, setShowModal] = useState(false)
  const toggleToast = useSnackbarStore((state) => state.toggleToast)
  const [hasPendingInstallUi, setHasPendingInstallUi] = useState(false)

  const setInstallProgress = ({
    label,
    progress,
    tone = "neutral",
    autoHideMs,
  }) => {
    const prefix =
      progress >= 100 ? "100%" : progress > 0 ? `${Math.max(progress, 8)}%` : ""

    toggleToast({
      isActive: true,
      color:
        tone === "success" ? "green" : tone === "error" ? "red" : "loading",
      text: prefix ? `${prefix} ${label}` : label,
      durationMs: autoHideMs ?? (tone === "neutral" ? 30000 : 2200),
    })
  }

  const showInstallReadyIfPending = () => {
    if (!hasPendingInstallUi || hasShownInstallReadyToast) {
      return
    }

    hasShownInstallReadyToast = true
    setHasPendingInstallUi(false)
    setInstallProgress({
      label: t.pwa.installReady,
      progress: 100,
      tone: "success",
      autoHideMs: 1800,
    })
  }

  const handleWorkerStateChange = ({ phase, worker }) => {
    if (worker === "installing") {
      if (phase === "installing") {
        hasShownInstallReadyToast = false
        setHasPendingInstallUi(true)
        setInstallProgress({
          label: t.pwa.installingProgress,
          progress: 35,
        })
      }

      if (phase === "installed") {
        setInstallProgress({
          label: t.pwa.finalizingInstall,
          progress: 72,
        })
      }

      return
    }

    if (worker === "waiting") {
      setInstallProgress({
        label: t.pwa.finalizingInstall,
        progress: 84,
      })
      return
    }

    if (worker === "active" && phase === "activated") {
      showInstallReadyIfPending()
    }
  }

  const update = () => {
    event({
      action: "update_pwa_click",
    })
    const wb = window.workbox
    wb.addEventListener("controlling", () => {
      window.location.reload()
    })

    // Send a message to the waiting service worker, instructing it to activate.
    wb.messageSkipWaiting()
    setShowModal(false)
  }
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !("serviceWorker" in navigator) ||
      window.workbox === undefined
    ) {
      return
    }

    const wb = window.workbox
    const handleControllerChange = () => {
      showInstallReadyIfPending()
    }
    const handleActivated = () => {
      showInstallReadyIfPending()
    }

    // A common UX pattern for progressive web apps is to show a banner when a service worker has updated and waiting to install.
    // NOTE: MUST set skipWaiting to false in next.config.js pwa object
    // https://developers.google.com/web/tools/workbox/guides/advanced-recipes#offer_a_page_reload_for_users
    const promptNewVersionAvailable = () => {
      setShowModal(true)
      event({
        action: "update_pwa_modal_open",
      })
    }

    navigator.serviceWorker.addEventListener(
      "controllerchange",
      handleControllerChange,
    )
    wb.addEventListener("activated", handleActivated)
    wb.addEventListener("controlling", handleActivated)
    wb.addEventListener("waiting", promptNewVersionAvailable)

    const existingController = Boolean(navigator.serviceWorker.controller)

    if (!existingController) {
      hasShownInstallReadyToast = false
      setHasPendingInstallUi(true)
      setInstallProgress({
        label: t.pwa.installingProgress,
        progress: 16,
      })
    }

    wb.register()
      .then((registration) => {
        attachWorkerStateListeners(registration, handleWorkerStateChange)

        if (registration?.active && !navigator.serviceWorker.controller) {
          showInstallReadyIfPending()
        } else if (registration?.installing || registration?.waiting) {
          hasShownInstallReadyToast = false
          setHasPendingInstallUi(true)
          setInstallProgress({
            label: t.pwa.finalizingInstall,
            progress: 55,
          })
        }
      })
      .catch(() => {
        setHasPendingInstallUi(false)
        setInstallProgress({
          label: t.pwa.installFailed,
          progress: 100,
          tone: "error",
          autoHideMs: 2600,
        })
      })

    return () => {
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        handleControllerChange,
      )
      wb.removeEventListener("activated", handleActivated)
      wb.removeEventListener("controlling", handleActivated)
      wb.removeEventListener("waiting", promptNewVersionAvailable)
    }
  }, [
    t.pwa.finalizingInstall,
    t.pwa.installFailed,
    t.pwa.installReady,
    t.pwa.installingProgress,
    hasPendingInstallUi,
  ])

  return (
    <>
      <Modal
        modalOpen={showModal}
        close={() => setShowModal(false)}
        isCenter={isDesktop}
      >
        <div className="p-3 pb-4">
          <div className="text-center mt-3 pt-1">
            <IoSparkles className="color-yellow-light fa-3x" />
          </div>
          <h5 className="text-center mt-3 font-700 font-25 mb-4">
            {t.pwa.newVersion}
          </h5>
          <p className="boxed-text-l">{t.pwa.newVersionDescription}</p>
          <button
            onClick={update}
            className="close-menu btn btn-m btn-center-m button-s shadow-l rounded-s text-uppercase font-900 bg-green-light"
          >
            {t.pwa.update}
          </button>
        </div>
      </Modal>
    </>
  )
}
